/**
 * Job Detail Page
 * View details of a single job tracker
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { formatDateShort } from '../utils/formatting';
import { JobDashboard } from '../components/job/JobDashboard';
import { MaterialTicketsList } from '../components/job/MaterialTicketsList';
import { AddTicketDialog } from '../components/job/AddTicketDialog';
import { PermitsTab } from '../components/job/PermitsTab';
import { InvoiceList } from '../components/invoice/InvoiceList';

// Job-related types (matching backend/admin structure)
export type JobStatus = 'active' | 'complete';

export interface Job {
  id: string;
  plan_id: string;
  status: JobStatus;
  project_name: string;
  project_address: string;
  start_date: string; // ISO date string
  target_completion_date?: string | null; // ISO date string
  target_diversion_rate: number;
  created_at: string; // ISO date string
  updated_at: string; // ISO date string
  last_accessed_at: string; // ISO date string
}

export interface JobResponse {
  job: Job;
}

export function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    location.state?.message || null
  );
  const [activeTab, setActiveTab] = useState<'dashboard' | 'tickets' | 'permits' | 'invoices'>('dashboard');
  const [showAddTicket, setShowAddTicket] = useState(false);
  const [ticketRefreshKey, setTicketRefreshKey] = useState(0);

  useEffect(() => {
    const loadJob = async () => {
      if (!id) return;

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/v1/jobs/${id}`
        );

        if (!response.ok) {
          throw new Error('Failed to load job');
        }

        const data: JobResponse = await response.json();
        setJob(data.job);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load job');
      } finally {
        setIsLoading(false);
      }
    };

    loadJob();
  }, [id]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Set active tab from location state if provided
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const formatTimestamp = (isoDate: string | null | undefined): string => {
    if (!isoDate) return 'N/A';
    return formatDateShort(isoDate);
  };

  const getStatusBadgeClass = (status: JobStatus): string => {
    return status === 'active'
      ? 'bg-accent-100 text-accent-700'
      : 'bg-neutral-200 text-neutral-700';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-neutral-600">Loading job...</p>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="card text-center py-12">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">Job Not Found</h2>
        <p className="text-neutral-600 mb-6">{error || 'The requested job could not be found'}</p>
        <button onClick={() => navigate('/')} className="btn-primary">
          Home
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-accent-50 border border-accent-200 rounded-lg flex items-center">
          <svg
            className="w-5 h-5 text-accent-600 mr-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-accent-900">{successMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-neutral-900">
                {job.project_name}
              </h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(job.status)}`}>
                {job.status === 'active' ? 'Active' : 'Complete'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-neutral-600">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span>{job.project_address}</span>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/plans/${job.plan_id}`)}
              className="btn-secondary"
            >
              View Plan
            </button>
            <button onClick={() => navigate('/')} className="btn-secondary">
              Home
            </button>
          </div>
        </div>

        {/* Project Timeline */}
        <div className="flex flex-wrap gap-4 text-sm text-neutral-600">
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>Started {formatTimestamp(job.start_date)}</span>
          </div>
          {job.target_completion_date && (
            <div className="flex items-center gap-2">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>Target {formatTimestamp(job.target_completion_date)}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            <span>Target Diversion: {job.target_diversion_rate}%</span>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-neutral-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'dashboard'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveTab('tickets')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tickets'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
            }`}
          >
            Tickets
          </button>
          <button
            onClick={() => setActiveTab('permits')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'permits'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
            }`}
          >
            Permits
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'invoices'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
            }`}
          >
            Invoices
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mb-8">
        {activeTab === 'dashboard' && (
          <JobDashboard jobId={id!} refreshInterval={30000} />
        )}

        {activeTab === 'tickets' && (
          <>
            <div className="mb-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-neutral-900">Material Tickets</h2>
              <button
                onClick={() => setShowAddTicket(true)}
                className="btn-primary"
              >
                <svg
                  className="w-5 h-5 mr-2 inline"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Ticket
              </button>
            </div>
            <MaterialTicketsList jobId={id!} key={ticketRefreshKey} />
            <AddTicketDialog
              jobId={id!}
              open={showAddTicket}
              onClose={() => setShowAddTicket(false)}
              onTicketAdded={() => {
                setTicketRefreshKey((k) => k + 1);
                setShowAddTicket(false);
              }}
            />
          </>
        )}

        {activeTab === 'permits' && <PermitsTab jobId={id!} />}

        {activeTab === 'invoices' && <InvoiceList jobId={id!} userRole="applicant" />}
      </div>

      {/* Job Metadata */}
      <div className="mt-6 text-sm text-neutral-600 text-center">
        <p>
          Job Created: {formatTimestamp(job.created_at)} | Last Updated:{' '}
          {formatTimestamp(job.updated_at)}
        </p>
      </div>
    </div>
  );
}
